import React, { useState, useMemo, useEffect } from "react";
import InfoWithTooltip from "./InfoWithTooltip";
import GoogleStatusBlock from "./GoogleStatusBlock";

const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";

const apiHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  "X-CSRF-Token": getCsrfToken(),
});

const inputStyle = { width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 4, fontSize: 14, boxSizing: "border-box" };
const MIN_HEADLINES = 3;
const MAX_HEADLINES = 15;
const MIN_DESCRIPTIONS = 2; // Google RSA requires 2-4 descriptions
const MAX_DESCRIPTIONS = 4;

// Map Google Ad field labels to ai_explanations keys from Stage 3b (headline_1..15, description_1..4)
const AD_EXPLANATION_KEYS = {
  "Ad name": "ad.name",
  "Ad type": "ad.ad_type",
  "Path 1": "ad.path1",
  "Path 2": "ad.path2",
  "Final URL": "ad.final_urls",
};
[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].forEach((n) => {
  AD_EXPLANATION_KEYS[`Headline ${n}`] = `ad.headline_${n}`;
});
[1, 2, 3, 4].forEach((n) => {
  AD_EXPLANATION_KEYS[`Description ${n}`] = `ad.description_${n}`;
});

const displayValue = (v) => (v != null && v !== "" ? String(v) : "—");

const GoogleAdsCreatePanel = ({ campaign, onRefetch, toast }) => {
  const adGroups = campaign?.ad_groups || [];
  const explanations = campaign?.ai_explanations || {};
  const [publishing, setPublishing] = useState(false);
  const [syncingStatusAdId, setSyncingStatusAdId] = useState(null);

  const getExplanation = (label) => {
    const key = AD_EXPLANATION_KEYS[label];
    if (key && explanations[key]) return explanations[key];
    if (label?.startsWith("Headline ")) return explanations["ad.headlines"];
    if (label?.startsWith("Description ")) return explanations["ad.descriptions"];
    return null;
  };

  const FieldLabel = ({ label }) => (
    <span className="ga-overview-panel__field-label-wrap">
      <span className="cr-field__label">{label}</span>
      {getExplanation(label) && <InfoWithTooltip text={getExplanation(label)} />}
    </span>
  );

  // Flatten all ads with global index (like Meta Creative tab)
  const allAds = useMemo(() => {
    const list = [];
    adGroups.forEach((ag) => {
      (ag.ads || []).forEach((ad) => {
        list.push({ ...ad, adGroupName: ag.name, adGroupId: ag.id });
      });
    });
    return list;
  }, [adGroups]);

  const [selectedAdIndex, setSelectedAdIndex] = useState(0);
  const [form, setForm] = useState({
    name: "",
    headlines: [{ text: "" }, { text: "" }, { text: "" }],
    descriptions: [{ text: "" }, { text: "" }],
    path1: "",
    path2: "",
    final_urls: "",
  });
  const [saving, setSaving] = useState(false);

  const selectedAd = allAds[selectedAdIndex];

  const loadAdIntoForm = (ad) => {
    if (!ad) return;
    const headlines = (ad.headlines || []).map((h) => ({ text: typeof h === "string" ? h : h?.text || "" }));
    while (headlines.length < MIN_HEADLINES) headlines.push({ text: "" });
    const descriptions = (ad.descriptions || []).map((d) => ({ text: typeof d === "string" ? d : d?.text || "" }));
    while (descriptions.length < MIN_DESCRIPTIONS) descriptions.push({ text: "" });
    setForm({
      name: ad.name || "",
      headlines,
      descriptions,
      path1: ad.path1 || "",
      path2: ad.path2 || "",
      final_urls: (ad.final_urls || [])[0] || "",
    });
  };

  useEffect(() => {
    if (selectedAd) loadAdIntoForm(selectedAd);
  }, [selectedAdIndex, selectedAd?.id]);

  const buildPayload = () => {
    const headlines = form.headlines.map((h) => ({ text: (h.text || "").trim() })).filter((h) => h.text);
    const descriptions = form.descriptions.map((d) => ({ text: (d.text || "").trim() })).filter((d) => d.text);
    return {
      name: form.name.trim(),
      headlines,
      descriptions,
      path1: form.path1.trim() || undefined,
      path2: form.path2.trim() || undefined,
      final_urls: form.final_urls.trim() ? [form.final_urls.trim()] : [],
    };
  };

  const saveAd = async () => {
    if (!selectedAd?.id) return;
    const payload = buildPayload();
    if (payload.headlines.length < MIN_HEADLINES) {
      toast?.error(`At least ${MIN_HEADLINES} headlines are required.`);
      return;
    }
    if (payload.descriptions.length < MIN_DESCRIPTIONS) {
      toast?.error(`At least ${MIN_DESCRIPTIONS} descriptions are required (Google Ads allows 2-4).`);
      return;
    }
    const res = await fetch(`/api/google_ads/ads/${selectedAd.id}`, {
      method: "PATCH",
      headers: apiHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (res.ok) {
      toast?.success("Ad updated.");
      onRefetch?.();
    } else {
      toast?.error(data.errors?.join(" ") || "Failed to update ad");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    saveAd().finally(() => setSaving(false));
  };

  const updateHeadline = (i, text) => {
    setForm((f) => ({
      ...f,
      headlines: f.headlines.map((h, j) => (j === i ? { ...h, text } : h)),
    }));
  };
  const addHeadline = () => {
    if (form.headlines.length >= MAX_HEADLINES) return;
    setForm((f) => ({ ...f, headlines: [...f.headlines, { text: "" }] }));
  };
  const removeHeadline = (index) => {
    if (form.headlines.length <= MIN_HEADLINES) return;
    setForm((f) => ({
      ...f,
      headlines: f.headlines.filter((_, i) => i !== index),
    }));
  };
  const updateDescription = (i, text) => {
    setForm((f) => ({
      ...f,
      descriptions: f.descriptions.map((d, j) => (j === i ? { ...d, text } : d)),
    }));
  };
  const addDescription = () => {
    if (form.descriptions.length >= MAX_DESCRIPTIONS) return;
    setForm((f) => ({ ...f, descriptions: [...f.descriptions, { text: "" }] }));
  };
  const removeDescription = (index) => {
    if (form.descriptions.length <= MIN_DESCRIPTIONS) return;
    setForm((f) => ({
      ...f,
      descriptions: f.descriptions.filter((_, i) => i !== index),
    }));
  };

  const handleSyncAdStatus = async () => {
    if (!selectedAd?.id) return;
    setSyncingStatusAdId(selectedAd.id);
    try {
      const res = await fetch(`/api/google_ads/ads/${selectedAd.id}/sync_status`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data?.id) {
        onRefetch?.();
        toast?.success("Ad, ad group and campaign status synced.");
      } else {
        toast?.error(data?.error || "Failed to sync status.");
      }
    } catch (e) {
      toast?.error("Failed to sync status.");
      console.error(e);
    } finally {
      setSyncingStatusAdId(null);
    }
  };

  const handleApproveAndSchedule = async () => {
    // Publish only the currently selected ad (like Meta Ads), not all draft ads
    if (!selectedAd?.id) return;
    const isDraftOrFailed = !selectedAd.google_ad_id && (selectedAd.status === "draft" || selectedAd.status === "failed");
    if (!isDraftOrFailed) {
      toast?.info("This ad is already published or scheduled.");
      return;
    }
    setPublishing(true);
    try {
      const res = await fetch(`/api/google_ads/ads/${selectedAd.id}/publish`, {
        method: "POST",
        headers: apiHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      onRefetch?.();
      if (res.ok && data.success) {
        toast?.success("Ad published to Google Ads.");
      } else {
        toast?.error(data.error || "Failed to publish ad to Google Ads.");
      }
    } catch (e) {
      toast?.error("Failed to publish ad.");
    } finally {
      setPublishing(false);
    }
  };

  if (!campaign) return null;

  // Empty state
  if (adGroups.length === 0) {
    return (
      <div className="cr-panel cr-panel--creative">
        <div className="cr-panel__empty">
          <p>Add ad groups in the Ad Group tab first.</p>
        </div>
      </div>
    );
  }

  if (allAds.length === 0) {
    return (
      <div className="cr-panel cr-panel--creative">
        <div className="cr-panel__empty">
          <p>No ads yet.</p>
        </div>
      </div>
    );
  }

  const adStatus = (selectedAd?.status || "").toLowerCase();
  const isAdDraftOrFailed = adStatus === "draft" || adStatus === "failed";

  return (
    <div className="cr-panel cr-panel--creative">
      <div className="cr-creative-selector" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px", marginBottom: 12 }}>
        <span className="cr-creative-selector__label">Viewing ad:</span>
        <div className="cr-creative-selector__pills" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {allAds.map((ad, index) => (
            <button
              key={ad.id}
              type="button"
              className={`cr-creative-selector__pill ${selectedAdIndex === index ? "cr-creative-selector__pill--active" : ""}`}
              onClick={() => setSelectedAdIndex(index)}
            >
              {index + 1}
            </button>
          ))}
        </div>
        {selectedAd?.adGroupName && (
          <span style={{ fontSize: "0.875rem", color: "#6b7280", marginLeft: 8 }}>
            Ad group: <strong style={{ color: "#1f2937" }}>{selectedAd.adGroupName}</strong>
          </span>
        )}
      </div>

      {isAdDraftOrFailed ? (
        <form onSubmit={handleSubmit} className="cr-panel__fields" style={{ width: "100%" }}>
          <div className="cr-field">
            <div className="cr-field__header"><FieldLabel label="Ad name" /></div>
            <div className="cr-field__value">
              <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. RSA - Main" style={inputStyle} />
            </div>
          </div>
          <div className="cr-field">
            <div className="cr-field__header"><FieldLabel label="Ad type" /></div>
            <div className="cr-field__value" style={{ fontWeight: 500 }}>
              {selectedAd?.ad_type || "RESPONSIVE_SEARCH_AD"}
            </div>
          </div>
          <div className="cr-panel__section" style={{ marginTop: 8 }}>
            <h3 className="cr-panel__section-title">Responsive search ad info</h3>
          </div>
          {form.headlines.map((h, i) => (
            <div key={i} className="cr-field" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="cr-field__header"><FieldLabel label={`Headline ${i + 1}`} /></div>
                <div className="cr-field__value">
                  <input type="text" value={h.text || ""} onChange={(e) => updateHeadline(i, e.target.value)} placeholder={`Headline ${i + 1} (max 30 chars)`} maxLength={30} style={inputStyle} />
                </div>
              </div>
              {form.headlines.length > MIN_HEADLINES && (
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeHeadline(i)} style={{ marginTop: 28 }} aria-label="Remove headline">Remove</button>
              )}
            </div>
          ))}
          {form.headlines.length < MAX_HEADLINES && (
            <div className="cr-field">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addHeadline}>
                + Add headline
              </button>
              <span style={{ fontSize: "0.8rem", color: "#6b7280", marginLeft: 8 }}>(min {MIN_HEADLINES}, max {MAX_HEADLINES})</span>
            </div>
          )}
          {form.descriptions.map((d, i) => (
            <div key={i} className="cr-field" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="cr-field__header"><FieldLabel label={`Description ${i + 1}`} /></div>
                <div className="cr-field__value">
                  <input type="text" value={d.text || ""} onChange={(e) => updateDescription(i, e.target.value)} placeholder={`Description ${i + 1} (max 90 chars)`} maxLength={90} style={inputStyle} />
                </div>
              </div>
              {form.descriptions.length > MIN_DESCRIPTIONS && (
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeDescription(i)} style={{ marginTop: 28 }} aria-label="Remove description">Remove</button>
              )}
            </div>
          ))}
          {form.descriptions.length < MAX_DESCRIPTIONS && (
            <div className="cr-field">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addDescription}>
                + Add description
              </button>
              <span style={{ fontSize: "0.8rem", color: "#6b7280", marginLeft: 8 }}>(min {MIN_DESCRIPTIONS}, max {MAX_DESCRIPTIONS} required by Google)</span>
            </div>
          )}
          <div className="cr-field">
            <div className="cr-field__header"><FieldLabel label="Path 1" /></div>
            <div className="cr-field__value">
              <input type="text" value={form.path1} onChange={(e) => setForm((f) => ({ ...f, path1: e.target.value }))} placeholder="e.g. rails" style={inputStyle} />
            </div>
          </div>
          <div className="cr-field">
            <div className="cr-field__header"><FieldLabel label="Path 2" /></div>
            <div className="cr-field__value">
              <input type="text" value={form.path2} onChange={(e) => setForm((f) => ({ ...f, path2: e.target.value }))} placeholder="e.g. developers" style={inputStyle} />
            </div>
          </div>
          <div className="cr-field">
            <div className="cr-field__header"><FieldLabel label="Final URL" /></div>
            <div className="cr-field__value">
              <input type="url" value={form.final_urls} onChange={(e) => setForm((f) => ({ ...f, final_urls: e.target.value }))} placeholder="https://..." style={inputStyle} />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12, width: "100%" }}>
            <button type="submit" className="btn btn-sm ga-btn-save" disabled={saving} style={{ width: "100%" }}>{saving ? "Saving…" : "Save changes"}</button>
            <button
              type="button"
              className="btn btn-sm ga-btn-approve"
              disabled={publishing || saving}
              onClick={handleApproveAndSchedule}
              style={{ width: "100%" }}
            >
              {publishing ? "Starting…" : "Approve and Schedule"}
            </button>
          </div>
        </form>
      ) : (
        <div className="cr-panel__fields" style={{ width: "100%" }}>
          {selectedAd?.google_ad_id && (
            <GoogleStatusBlock
              title="Google status"
              rows={[
                { label: "Status", value: selectedAd.status },
                { label: "Primary status", value: selectedAd.primary_status },
                { label: "Approval status", value: selectedAd.approval_status },
              ]}
              reasons={Array.isArray(selectedAd.primary_status_reasons) ? selectedAd.primary_status_reasons : []}
              onSync={handleSyncAdStatus}
              syncing={syncingStatusAdId === selectedAd.id}
              showSyncButton
            />
          )}
          <div className="cr-field">
            <div className="cr-field__header"><FieldLabel label="Ad name" /></div>
            <div className="cr-field__value" style={{ fontWeight: 500 }}>{selectedAd?.name || "—"}</div>
          </div>
          <div className="cr-field">
            <div className="cr-field__header"><FieldLabel label="Ad type" /></div>
            <div className="cr-field__value" style={{ fontWeight: 500 }}>{selectedAd?.ad_type || "RESPONSIVE_SEARCH_AD"}</div>
          </div>
          <div className="cr-panel__section" style={{ marginTop: 8 }}>
            <h3 className="cr-panel__section-title">Responsive search ad info</h3>
          </div>
          {(() => {
            const list = form.headlines.length > 0 ? form.headlines : (selectedAd?.headlines || []).map((h) => ({ text: typeof h === "string" ? h : h?.text || "" }));
            const toShow = list.length > 0 ? list : [{ text: "" }];
            return toShow.map((h, i) => (
              <div key={i} className="cr-field">
                <div className="cr-field__header"><FieldLabel label={`Headline ${i + 1}`} /></div>
                <div className="cr-field__value" style={{ fontWeight: 500 }}>{h?.text || "—"}</div>
              </div>
            ));
          })()}
          {(() => {
            const list = form.descriptions.length > 0 ? form.descriptions : (selectedAd?.descriptions || []).map((d) => ({ text: typeof d === "string" ? d : d?.text || "" }));
            const toShow = list.length > 0 ? list : [{ text: "" }, { text: "" }];
            return toShow.map((d, i) => (
              <div key={i} className="cr-field">
                <div className="cr-field__header"><FieldLabel label={`Description ${i + 1}`} /></div>
                <div className="cr-field__value" style={{ fontWeight: 500 }}>{d?.text || "—"}</div>
              </div>
            ));
          })()}
          <div className="cr-field">
            <div className="cr-field__header"><FieldLabel label="Path 1" /></div>
            <div className="cr-field__value" style={{ fontWeight: 500 }}>{selectedAd?.path1 || form.path1 || "—"}</div>
          </div>
          <div className="cr-field">
            <div className="cr-field__header"><FieldLabel label="Path 2" /></div>
            <div className="cr-field__value" style={{ fontWeight: 500 }}>{selectedAd?.path2 || form.path2 || "—"}</div>
          </div>
          <div className="cr-field">
            <div className="cr-field__header"><FieldLabel label="Final URL" /></div>
            <div className="cr-field__value" style={{ fontWeight: 500 }}>{(selectedAd?.final_urls || [])[0] || form.final_urls || "—"}</div>
          </div>
          <div style={{ padding: "0.75rem", backgroundColor: "#F5F5F5", borderRadius: 6, textAlign: "center", color: "#525250", fontSize: "0.875rem", marginTop: 12 }}>
            {adStatus === "scheduled" ? "Ad is scheduled" : adStatus === "published" ? "Ad is published" : `Ad status: ${selectedAd?.status ?? "—"}`}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleAdsCreatePanel;
